import { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useMutation } from '@apollo/client/react';
import { UPDATE_HISTORICAL_PRICE, GET_HISTORICAL_PRICE } from './historical.graphql';
import moment from 'moment';
import { HistoricalGuidePriceModel } from './overview';
import { DateTimePicker } from '@mui/x-date-pickers';

export const UpdateHistoricalPriceDialog: FC<{ historical: HistoricalGuidePriceModel; onClose(): void; setBusy(busy: boolean): void }> = ({ historical, onClose, setBusy }) => {
  const defaultTimeOfChange = moment().add(1, 'years').startOf('year');

  const [price, setPrice] = useState(historical.price);
  const [timeOfChange, setTimeOfChange] = useState(defaultTimeOfChange);

  // Follow the same pattern - no variables in useMutation
  const [updateValue, { loading, error }] = useMutation<any, any>(UPDATE_HISTORICAL_PRICE, {
    onCompleted: (data) => {
      console.log('Historical price updated successfully:', data);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to update historical price:', error);
    },
    refetchQueries: [{ query: GET_HISTORICAL_PRICE }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    setBusy(loading);
  }, [loading, setBusy]);

  const priceChanged = price !== historical.price;

  // Follow the same pattern - pass variables in the mutation call
  const onCommit = () => {
    updateValue({ 
      variables: { 
        input: {
          timeOfChange: timeOfChange.toISOString(),
          price: Number(price)
        }
      } 
    });
  };

  const close = () => {
    setPrice(historical.price);
    onClose();
  };

  if (!historical) return null;

  const onTimeOfChangeChanged = (newValue) => {
    if (newValue) setTimeOfChange(newValue);
    else setTimeOfChange(defaultTimeOfChange);
  };

  return (
    <Dialog onClose={close} aria-labelledby="dialog-title" open>
      <DialogTitle id="dialog-title">
        Endre pris - Historisk dokument
        <IconButton
          aria-label="close"
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(1),
            top: (theme) => theme.spacing(1),
            color: (theme) => theme.palette.grey[500],
          }}
          onClick={close}
          size="large">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" variant="body2" gutterBottom>
            Feil ved oppdatering: {error.message}
          </Typography>
        )}
        <form>
          <div
            style={{
              background: 'rgb(232, 244, 253)',
              borderRadius: 4,
              padding: 5,
              marginBottom: 25,
            }}>
            <Typography gutterBottom>Når skal endringen gjelde fra?</Typography>
            <div>
              <DateTimePicker label="tidspunkt" value={timeOfChange} onChange={onTimeOfChangeChanged} />
            </div>
          </div>
          <Grid container direction="row" spacing={2} sx={{justifyContent: 'center', alignItems: 'center'}}>
            <Grid>
              <TextField
                variant="outlined"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">kr</InputAdornment>,
                  },
                  htmlInput: { min: 0 },
                }}
                label="Ny pris"
                helperText="Beløp for 1 historisk dokument"
                type="number"
                margin="normal"
                required
                value={price}
                onChange={(ev) => setPrice(Number(ev.target.value))}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} color="secondary">
          Avbryt
        </Button>
        <Button onClick={onCommit} disabled={!priceChanged || loading} color="primary" variant="contained">
          {loading ? 'Oppdaterer...' : 'Endre pris'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};